import { useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sparkles, ListPlus } from "lucide-react";

/** Iter 172 · Bulk-add brands by pasting names, one per line. */

export default function BrandBulkAddDialog({ wsId, clients, onClose, onCreated }) {
  const [text, setText] = useState("");
  const [clientId, setClientId] = useState("_none");
  const [industry, setIndustry] = useState("");
  const [busy, setBusy] = useState(false);

  const names = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  const unique = Array.from(new Set(names.map((n) => n.toLowerCase())))
                       .map((low) => names.find((n) => n.toLowerCase() === low));

  const save = async () => {
    if (unique.length === 0) {
      toast.error("Paste at least one brand name");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/bulk-create-by-names`,
        { names: unique,
          client_id: clientId === "_none" ? null : clientId,
          industry: industry || null });
      const { created_count, skipped_count, skipped } = r.data;
      toast.success(
        `Created ${created_count}${skipped_count ? ` · ${skipped_count} duplicate(s) skipped` : ""}`);
      if (skipped_count) {
        toast.message("Skipped duplicates", {
          description: (skipped || []).slice(0, 8).join(", ")
                        + (skipped.length > 8 ? "…" : ""),
        });
      }
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk create failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="brand-bulk-add-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListPlus className="w-5 h-5" /> Bulk add brands
          </DialogTitle>
          <DialogDescription>
            Paste one brand name per line — up to 200. Duplicates (case-insensitive)
            are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Brand names</Label>
            <textarea value={text} onChange={(e) => setText(e.target.value)}
                        rows={9}
                        placeholder={"Nike\nAdidas\nPuma\nReebok"}
                        className="w-full text-sm p-2 border border-border rounded-none font-mono"
                        data-testid="brand-bulk-add-textarea" />
            <div className="mt-1 text-[11px] text-muted-foreground">
              <span data-testid="brand-bulk-add-count">{unique.length}</span> unique
              name{unique.length === 1 ? "" : "s"} detected.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Client (optional)</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="h-9 rounded-none" data-testid="brand-bulk-client-select">
                  <SelectValue placeholder="No client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No client</SelectItem>
                  {(clients || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Industry (optional)</Label>
              <input value={industry} onChange={(e) => setIndustry(e.target.value)}
                       placeholder="e.g. Apparel"
                       className="h-9 w-full border border-border rounded-none px-2 text-sm bg-background"
                       data-testid="brand-bulk-industry-input" />
            </div>
          </div>

          <div className="text-[11px] text-muted-foreground bg-muted/40 border border-border p-2 rounded-none">
            <Sparkles className="w-3 h-3 inline mr-1 text-primary" />
            Applies the chosen client / industry to <b>every</b> brand created.
            Each brand starts with empty strategy sections you can fill later or
            auto-generate via the &ldquo;AI brief from website&rdquo; tool on the brand detail page.
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy || unique.length === 0}
                    data-testid="brand-bulk-add-save">
            {busy ? "Creating…" : `Create ${unique.length} brand${unique.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
